import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Share2, PenLine, Target, BarChart3, Palette, TrendingUp,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";

function AnimatedSection({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className={className}>
      {children}
    </motion.div>
  );
}

const services = [
  { icon: Share2, title: "إدارة حسابات التواصل", desc: "إدارة شاملة واحترافية لجميع منصات التواصل الاجتماعي مع تفاعل مستمر مع الجمهور" },
  { icon: PenLine, title: "صناعة المحتوى", desc: "محتوى إبداعي مخصص يعكس هوية علامتك التجارية ويجذب جمهورك المستهدف" },
  { icon: Target, title: "الحملات الإعلانية", desc: "حملات إعلانية مدفوعة مدروسة تحقق أعلى عائد على الاستثمار" },
  { icon: Palette, title: "بناء الهوية التجارية", desc: "تصميم هوية بصرية متكاملة تميز علامتك التجارية في السوق" },
  { icon: TrendingUp, title: "الاستراتيجيات التسويقية", desc: "خطط تسويقية شاملة مبنية على بيانات وتحليلات دقيقة" },
  { icon: BarChart3, title: "تحليل الأداء والتقارير", desc: "تقارير دورية مفصلة توضح الأداء وتوجه القرارات المستقبلية" },
];

export default function MarketingPage() {
  const settings = useSettings();
  return (
    <div data-testid="marketing-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={settings.marketing_image || "https://images.pexels.com/photos/9034280/pexels-photo-9034280.jpeg?auto=compress&cs=tinysrgb&w=1920"} alt="marketing" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111112]/50 to-[#111112]" />
        </div>
        <div className="absolute top-1/3 right-0 w-80 h-80 bg-primary/10 rounded-full blur-[120px]" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 text-primary text-sm font-medium mb-4">قسم التسويق</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Zain'] text-white leading-tight mb-4">
              نسوّق <span className="text-primary">لنجاحك</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              نقدم حلولًا تسويقية متكاملة تساعدك على الوصول لجمهورك المستهدف وبناء حضور رقمي قوي ومؤثر.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <AnimatedSection className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold font-['Zain'] text-white">خدماتنا التسويقية</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <AnimatedSection key={i}>
                  <div className="bg-[#161618] border border-white/5 rounded-xl p-6 hover:border-primary/20 transition-all group" data-testid={`marketing-service-${i}`}>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon size={22} className="text-primary" />
                    </div>
                    <h3 className="text-lg font-bold font-['Zain'] text-white mb-2">{s.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0d0d0e]">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <AnimatedSection>
            <h2 className="text-2xl md:text-4xl font-bold font-['Zain'] text-white mb-6">
              جاهز لتطوير تسويقك الرقمي؟
            </h2>
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-[#E54D2E] text-white px-8 red-glow" data-testid="marketing-cta">
                احصل على استشارة مجانية
                <ArrowLeft size={16} className="ms-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
